/**
 * @typedef AccessGood
 * @property {string} [modelName]
 * @property {string} [modelId]
 * @property {string} [type]
 * @property {string} [image]
 * @property {string} [link]
 * @property {string} [id]
 * @property {boolean} [soon]
 */
/**
 * @typedef Access
 * @property {string} id
 * @property {string} slug
 * @property {string} link
 * @property {Object.<string, string>} title
 * @property {Object.<string, string>} paymentDescription
 * @property {AccessGood[]} [goods]
 * @property {'simple'|'subscription'} type
 * @property {'month'|'year'} [subscribe_periodicity]
 * @property {string} company
 * @property {string} [thumbnail]
 * @property {boolean} hidden
 * @property {boolean} [isContentSubscription]
 * @property {number} [fullPrice]
 * @property {number} price
 * @property {boolean} useFullPrice
 * @property {string} [saleStart] date string
 * @property {string} [saleEnd] date string
 * @property {{ image: string }} [gain]
 * @property {number} [days]
 * @property {number} [monthes]
 * @property {boolean} [disableReferral]
 * @property {boolean} [multipleBuy]
 * @property {string[]} [certificateOf]
 * @property {string[]} [relatedAccesses]
 * @property {number} [tariffLevel]
 * @property {string[]} [relatedTariffs]
 */
/**
 * @typedef AccessManager
 * @property {string} id
 * @property {string} user
 * @property {Access} accessTariff
 * @property {Access} accessSubscription
 * @property {string} startDate date string
 * @property {string} endDate date string
 * @property {string} [expiredAt] date string
 * @property {'active'|'expired'|'ended'} status
 * @property {number} inactiveDays
 */
/**
 * @typedef AccessManagerStoreState
 * @property {AccessManager[]} managers
 * @property {{ maxInactiveDays: number }} config
 * @property {boolean} hydrated
 */
import { defineStore } from 'pinia'

export const useAccessManagerStore = defineStore({
    id: 'accessManager',

    state () {
        return {
            /** @type {AccessManager[]} */
            managers: [],

            /** @type {{ maxInactiveDays: number }} */
            config: {
                maxInactiveDays: 90,
            },

            /** @type {boolean} */
            hydrated: false,
        }
    },

    getters: {
        /**
         * Return all active AccessManagers
         *
         * @param {AccessManagerStoreState} state
         * @returns {AccessManager[]}
         */
        active (state) {
            return state.managers;
        },

        /**
         * Return all discounted AccessManagers
         *
         * @param {AccessManagerStoreState} state
         * @returns {AccessManager[]}
         */
        discounted (state) {
            return state.managers.filter(m => m.accessSubscription.price < m.accessTariff.price);
        },

        /**
         * Return maximum number of inactive days
         *
         * This value should be the same on all managers, but just in case,
         * we show the maximum value (which means the least days left until
         * expiration).
         *
         * @param {AccessManagerStoreState} state
         * @returns {number}
         */
        inactiveDays (state) {
            return state.managers.reduce((value, manager) => {
                return Math.max(value, manager.inactiveDays);
            }, 0);
        },

        /**
         * Return latest manager end date
         *
         * This value should be the same on all managers, but just in case,
         * we show the latest value (which means the latest date when
         * an access manager ends and restarts, resetting inactive days).
         *
         * @param {AccessManagerStoreState} state
         * @returns {Date}
         */
        endDate (state) {
            return state.managers.reduce((value, manager) => {
                if (!value) return new Date(manager.endDate);
                const endDate = new Date(manager.endDate);
                return endDate > value ? endDate : value;
            }, false);
        },
    },

    actions: {
        async hydrate () {
            // Prevent requests for non logged-in users
            if (!this.$nuxt.$auth.user) return this.hydrated = true;

            let data = await this.$nuxt.$axios.$get('/accessmanager/active')
            .catch(() => {});
            
            if (data && data.managers) this.managers = data.managers;
            if (data && data.config) this.config = Object.assign(this.config, data.config);
            this.hydrated = true;
        },
    },
});
